import { ReactNode, forwardRef } from "react";
import { ChevronRight, LucideIcon } from "lucide-react";
import { cn } from "@/utils/module_api";

interface ListContainerProps {
  children: ReactNode;
  className?: string;
}

export function ListContainer({ children, className }: ListContainerProps) {
  return (
    <div className={cn("bg-card rounded-lg divide-y divide-border overflow-hidden", className)}>
      {children}
    </div>
  );
}

interface ListItemProps {
  title: string;
  subtitle?: ReactNode;
  after?: ReactNode;
  media?: ReactNode;
  /** Icon element (already rendered JSX element) */
  icon?: ReactNode;
  link?: boolean;
  chevron?: boolean;
  showChevron?: boolean;
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
}

export const ListItem = forwardRef<HTMLDivElement, ListItemProps>(
  (
    {
      title,
      subtitle,
      after,
      media,
      icon,
      link = false,
      chevron,
      showChevron: showChevronProp,
      onClick,
      className,
      disabled = false,
    },
    ref
  ) => {
    const showChevronFinal = showChevronProp ?? chevron ?? link;
    const isClickable = onClick || link;

    return (
      <div
        ref={ref}
        className={cn(
          "flex items-center gap-3 px-4 py-3 bg-background",
          isClickable && !disabled && "cursor-pointer active:bg-accent hover:bg-accent/50",
          disabled && "opacity-50 cursor-not-allowed",
          className
        )}
        onClick={disabled ? undefined : onClick}
      >
        {media && <div className="flex-shrink-0">{media}</div>}
        {icon && (
          <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
            {icon}
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">{title}</div>
          {subtitle && (
            <div className="text-xs text-muted-foreground truncate">
              {subtitle}
            </div>
          )}
        </div>

        {after && (
          <div className="flex-shrink-0 text-sm text-muted-foreground">
            {after}
          </div>
        )}

        {showChevronFinal && (
          <ChevronRight className="flex-shrink-0 w-4 h-4 text-muted-foreground" />
        )}
      </div>
    );
  }
);

ListItem.displayName = "ListItem";
